import '../../utils/smartTooltip';
import React, { useState, useEffect } from 'react';
import FormUsersList from '../teams/FormUsersList';
import RefExtractionModal from './RefExtractionModal';
import LinkedProjectsModal, { useLinkedProjects } from './LinkedProjectsModal';
import { generateUUID } from '../../utils/HashUtils';
import { extractDocuments, getDroppedFiles, getPickedFiles } from '../../utils/ArchiveUtils';
import { usePermissions } from '../../utils/PermissionsContext';
const ARCHIVE_CATEGORY_LABELS = {
  pdfFiles: 'PDF',
  wordFiles: 'Word',
  textFiles: 'Text',
  pptFiles: 'PowerPoint',
  excelFiles: 'Excel',
  csvFiles: 'CSV'
};

const UnstructuredThreadForm = ({
  editingThread, 
  formData, 
  setFormData, 
  selectedUsers, 
  setSelectedUsers, 
  files, 
  setFiles,
  refExtractFiles,
  setRefExtractFiles,
  onSubmit, 
  onCancel,
  fromProject = false
}) => {
  const max_chars_for_tag = 70;
  const { email } = usePermissions();
  const [tagInput, setTagInput] = useState('');
const [hasFiles, setHasFiles] = useState(false);
const [showRefModal, setShowRefModal] = useState(false);
const [showLinkedProjects, setShowLinkedProjects] = useState(false);
const [isExtracting, setIsExtracting] = useState(false);
const [extractedCount, setExtractedCount] = useState(0);
const [extractSummary, setExtractSummary] = useState(null);
const [isDragOver, setIsDragOver] = useState(false);
const { projects: linkedProjects } = useLinkedProjects(editingThread?.id, !!editingThread);
const API_BASE_URL = '/pceapi/threads/unstructured';
// Track if any files are selected to show/hide RefExtractionModal
useEffect(() => {
  const filesSelected = Object.values(files).some(fileList => fileList && fileList.length > 0);
  setHasFiles(filesSelected);
}, [files]);
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
const sanitizeFileName = (fileName) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex);

  // Preserve letters, combining marks and numbers from every language.
  let sanitizedName = name.replace(/\s/g, '-')  // spaces to hyphens
                         .replace(/[^\p{L}\p{M}\p{N}\-.]/gu, '-')  // special chars to hyphens
                         .replace(/-+/g, '-');  // collapse multiple hyphens to single
  if (extension == '.pptx'){
                         sanitizedName = sanitizedName.slice(0, 40);}
  return sanitizedName + extension;
};
  // Add files to their categories, after the ones already chosen. A file already
  // in the category (same name and size) is not added twice.
  const addFilesToCategories = (filesByCategory) => {
  setFiles(prev => {
    const next = { ...prev };
    Object.entries(filesByCategory).forEach(([category, categoryFiles]) => {
      const existing = next[category] || [];
      const existingKeys = new Set(existing.map(item => `${item.file.name}:${item.file.size}`));
      const added = [];
      categoryFiles.forEach(file => {
        const sanitizedName = sanitizeFileName(file.name);
        const key = `${sanitizedName}:${file.size}`;
        if (existingKeys.has(key)) return;
        existingKeys.add(key);
        added.push({
          file: new File([file], sanitizedName, { type: file.type }),
          id: generateUUID()
        });
      });
      next[category] = [...existing, ...added];
    });
    return next;
  });
};

  // Handle file selection
  const handleFileChange = (e) => {
  const { name, files: selectedFiles } = e.target;
  if (selectedFiles.length > 0) {
    addFilesToCategories({ [name]: Array.from(selectedFiles) });
  }
  // Let the same file be picked again after the category is cleared
  e.target.value = '';
};

  // Remove every file of a category, and its reference extraction selection
  const handleClearCategory = (category) => {
  const oldFileIds = (files[category] || []).map(item => item.id);
  if (oldFileIds.length > 0) {
    setRefExtractFiles(prev => {
      const newSet = new Set(prev);
      oldFileIds.forEach(id => newSet.delete(id));
      return newSet;
    });
  }
  setFiles(prev => ({ ...prev, [category]: [] }));
};

  // Open zips and folders and send each document to its category's card
  const handleArchiveItems = async (items) => {
  if (items.length === 0) return;
  setIsExtracting(true);
  setExtractedCount(0);
  setExtractSummary(null);
  try {
    const { documents, skipped } = await extractDocuments(items, setExtractedCount);
    addFilesToCategories(documents);
    const addedCounts = Object.fromEntries(
      Object.entries(documents).map(([category, categoryFiles]) => [category, categoryFiles.length])
    );
    setExtractSummary({ addedCounts, skipped });
  } catch (error) {
    console.error('Could not extract documents:', error);
    setExtractSummary({ addedCounts: {}, skipped: [], error: 'Could not read the selected files' });
  } finally {
    setIsExtracting(false);
  }
};

  const handleArchivePick = (e) => {
  const items = getPickedFiles(e.target.files);
  e.target.value = '';
  handleArchiveItems(items);
};

  const handleArchiveDrop = async (e) => {
  e.preventDefault();
  setIsDragOver(false);
  if (isExtracting) return;
  const items = await getDroppedFiles(e.dataTransfer);
  handleArchiveItems(items);
};

  const renderFilePicker = (category, accept) => {
  const count = (files[category] || []).length;
  return (
    <div className="form-control form-control-sm choose-files d-flex align-items-center">
      <label className="choose-files-button mb-0">
        Choose Files
        <input
          type="file"
          name={category}
          onChange={handleFileChange}
          multiple
          accept={accept}
          hidden
        />
      </label>
      <span className="text-truncate flex-grow-1">
        {count > 0 ? `${count} ${count === 1 ? 'file' : 'files'} chosen` : 'No file chosen'}
      </span>
      {count > 0 && (
        <button
          type="button"
          className="btn-close ev-btn-text-only-fill p-0 mx-2"
          onClick={() => handleClearCategory(category)}
          title="Remove these files"
        ><i className="bi bi-x-circle"></i></button>
      )}
    </div>
  );
};

  // Handle access level change
  const handleAccessLevelChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, thread_access_level: value }));
    
    // If switching to PUBLIC, clear user access selections
    if (value === 'PUBLIC') {
      setSelectedUsers([]);
    }
  };

  // Handle user selection for access
  const handleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    } else {
      setSelectedUsers(prev => [...prev, userId]);
    }
  };

  // Saved meta tags are locked for users the backend says can't remove them
  // (a Dossier Development Lead on a role-based project): they can still add
  // tags, and remove the ones added in this edit. update_thread applies the
  // same rule and keeps saved tags whatever is sent. Only an explicit false
  // locks, so a record without the flag leaves the form as it was.
  const savedTags = (editingThread && editingThread.tags) || [];
  const lockSavedTags = Boolean(editingThread) && editingThread.can_remove_tags === false;
  const isLockedTag = (tag) => lockSavedTags && savedTags.includes(tag);

  // Handle adding a tag. Duplicates are matched case-insensitively, the way
  // document classification and Sync Tags compare them.
  const handleAddTag = () => {
    const trimmed = tagInput.trim().slice(0, max_chars_for_tag);
    const exists = formData.tags.some(tag => tag.trim().toLowerCase() === trimmed.toLowerCase());
    if (trimmed !== '' && !exists) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, trimmed]
      }));
      setTagInput('');
    }
  };
// Get total count of all files
const getTotalFilesCount = () => {
  return Object.values(files).reduce((acc, arr) => acc + (arr?.length || 0), 0);
};
  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    if (isLockedTag(tagToRemove)) return;
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle tag input keydown (for Enter key)
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="ev-card-2 w-100 ev-padding-xlarge">
      <p className="ev-med-p-dark mb-4">
        {editingThread ? 'Edit Data Folder Details' : 'Create Data Folder'}
      </p>
      {/* Reference Extraction Selection - Show when files are uploaded */}
      
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="col-6">
            <div className="form-group mb-3">
              <label className="ev-reg-p-dark mb-2">Data Folder Name <span className="text-danger">*</span></label>
              <input 
                type="text" 
                className="formField3" 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange} 
                required 
                disabled={fromProject && !!editingThread}
                title={fromProject && editingThread ? "Data Folder name can't be changed from here" : undefined}
              />
            </div>
            {/* <div className="row">
              <div className="col-5">
                <div className="form-group mb-3">
                  <label className="ev-reg-p-dark mb-1">Access Level <span className="text-danger">*</span></label>
                  <div className="btn-group custom-btn-group-outer w-100" role="group" aria-label="Access Level Selection">
                    <button
                      type="button"
                      className={`btn custom-btn-group-inner ${formData.thread_access_level === 'PUBLIC' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                      onClick={() => handleAccessLevelChange({ target: { name: 'thread_access_level', value: 'PUBLIC' } })}
                    >
                      Public
                    </button>
                    <button
                      type="button"
                      className={`btn custom-btn-group-inner ${formData.thread_access_level === 'PRIVATE' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                      onClick={() => handleAccessLevelChange({ target: { name: 'thread_access_level', value: 'PRIVATE' } })}
                    >
                      Private
                    </button>
                  </div>
                </div>
              </div>
            </div> */}
            <div className="form-group mb-4">
              <label className="ev-reg-p-dark mb-1">Meta Tags <span className="ev-small-p-blue"><i>(Optional)</i></span> {tagInput.length >= max_chars_for_tag && <span className="text-danger ev-fs-10 ms-2">Reached maximum {max_chars_for_tag} characters</span>}</label>
              <div className="d-flex mb-2">
                <input
                  type="text"
                  className="formField3 ev-btn-reg"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Enter tag and press Enter"
                  maxLength={max_chars_for_tag}
                />
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-bold-reg ev-radius-small ms-1"
                  onClick={handleAddTag}
                >
                  Add
                </button>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="ev-message-card3 ev-btn-small">
                    {tag}
                    {isLockedTag(tag) ? (
                      <span
                        className="ms-2 smart-tooltip"
                        data-tooltip="Only a Project Manager, the country head or an Admin can remove a saved tag"
                        aria-label="Saved tag, can't be removed"
                      ><i className="bi bi-lock"></i></span>
                    ) : (
                      <button
                        type="button"
                        className="btn-close ev-btn-text-only-fill p-0 ms-2 me-0"
                        onClick={() => handleRemoveTag(tag)}
                      ><i className ="bi bi-x-circle"></i></button>
                    )}
                  </span>
                ))}
              </div>
            </div>
            
            {formData.thread_access_level === 'PRIVATE' && (
              <div className="form-group mb-4">
                <label className="ev-reg-p-dark mb-1">User Access <span className="ev-small-p-blue"><i>(Optional)</i></span></label>
                <FormUsersList 
                  apiBaseUrl={API_BASE_URL}
                  selectedUsers={selectedUsers}
                  onSelectUser={handleUserSelection}
                  ownerEmail={editingThread ? editingThread.created_by_email : email}
                />
              </div>
            )}

            {editingThread && (
              <div className="form-group mb-4">
                <div className="ev-card-3 d-flex align-items-center justify-content-between gap-3 px-3 py-2 ev-radius-small border border-secondary border-opacity-25">
                  <span className="ev-reg-p-dark">
                    <i className="bi bi-link-45deg me-1"></i>
                    Linked Projects
                    <span className="ev-message-card1 ms-2">{linkedProjects.length}</span>
                  </span>
                  <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-btn-small text-nowrap"
                    onClick={() => setShowLinkedProjects(true)}
                  >
                    View
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="col-6">
            <div className="form-group mb-0">
  <div className="d-flex justify-content-between align-items-center mb-1">
    <label className="ev-reg-p-dark mb-0">Documents <span className="ev-small-p-blue"><i>(Optional)</i></span></label>
    {getTotalFilesCount() > 0 && (
      <button
        type="button"
        className="ev-btn-blue-outline-round ev-btn-small"
        onClick={() => setShowRefModal(true)}
      >
        <i className="bi bi-bookmark-check me-1"></i>
        Reference Extraction ({refExtractFiles.size}/{getTotalFilesCount()})
      </button>
    )}
  </div>
  <div className="row">
                <div className="col-12 mb-3">
                  <div
                    className={`ev-card-3 border-2 p-2 archive-drop-zone ${isDragOver ? 'drag-over' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
                    onDrop={handleArchiveDrop}
                  >
                    <div className="card-body p-1">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="ev-reg-p-dark">
                          <h6 className="ev-fs-12 mb-1">
                            <i className="bi bi-file-earmark-zip me-1"></i>
                            ZIP Files and Folders
                          </h6>
                          <p className="ev-fs-10 text-muted mb-0">
                            Drop or choose zips and folders, the documents inside go to their cards below
                          </p>
                        </div>
                        <div className="d-flex gap-2">
                          <label className={`choose-files-button mb-0 ${isExtracting ? 'disabled' : ''}`}>
                            Choose ZIP
                            <input type="file" accept=".zip" multiple hidden disabled={isExtracting} onChange={handleArchivePick} />
                          </label>
                          <label className={`choose-files-button mb-0 ${isExtracting ? 'disabled' : ''}`}>
                            Choose Folder
                            <input type="file" webkitdirectory="" multiple hidden disabled={isExtracting} onChange={handleArchivePick} />
                          </label>
                        </div>
                      </div>
                      {isExtracting && (
                        <p className="ev-fs-11 mb-0 mt-2">
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Extracting documents... {extractedCount} found
                        </p>
                      )}
                      {!isExtracting && extractSummary && (
                        <div className="ev-fs-11 mt-2">
                          {extractSummary.error ? (
                            <span className="text-danger">{extractSummary.error}</span>
                          ) : (
                            <span>
                              Added:{' '}
                              {Object.keys(extractSummary.addedCounts).length > 0
                                ? Object.entries(extractSummary.addedCounts)
                                    .map(([category, count]) => `${count} ${ARCHIVE_CATEGORY_LABELS[category]}`)
                                    .join(', ')
                                : 'no supported documents'}
                            </span>
                          )}
                          {extractSummary.skipped.length > 0 && (
                            <details className="mt-1">
                              <summary className="text-muted">Skipped {extractSummary.skipped.length}</summary>
                              <ul className="mb-0 ps-3 scrollable-element archive-skipped-list">
                                {extractSummary.skipped.map((item, index) => (
                                  <li key={index}>{item.path} ({item.reason})</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="ev-card-3 border-2 p-2">
                    <div className="card-body p-1">
                      <div className ="text-danger">
                        <h3 className="mb-2">
                        <i className="bi bi-file-earmark-pdf"></i>
                        </h3>
                        <h6 className="ev-fs-12 mb-1">
                        PDF Documents
                        </h6>
                      </div>
                      {renderFilePicker('pdfFiles', '.pdf')}
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="ev-card-3 border-2 p-2">
                    <div className="card-body p-1">
                      <div className ="text-primary">
                        <h3 className="mb-2">
                        <i className="bi bi-file-earmark-word"></i>
                        </h3>
                        <h6 className="ev-fs-12 mb-1">
                          Word Documents
                        </h6>
                      </div>
                      {renderFilePicker('wordFiles', '.doc,.docx')}
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="ev-card-3 border-2 p-2">
                    <div className="card-body p-1">
                      <div className ="text-secondary">
                        <h3 className="mb-2">
                        <i className="bi bi-file-earmark-text"></i>
                        </h3>
                        <h6 className="ev-fs-12 mb-1">
                        Text Documents
                        </h6>
                      </div>
                      {renderFilePicker('textFiles', '.txt')}
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="ev-card-3 border-2 p-2">
                    <div className="card-body p-1">
                      <div className ="text-orange">
                        <h3 className="mb-2">
                        <i className="bi bi-file-earmark-ppt"></i>
                        </h3>
                        <h6 className="ev-fs-12 mb-1">
                          Powerpoint Documents
                        </h6>
                      </div>
                      {renderFilePicker('pptFiles', '.ppt,.pptx')}
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="ev-card-3 border-2 p-2">
                    <div className="card-body p-1">
                      <div className ="text-success">
                        <h3 className="mb-2">
                        <i className="bi bi-file-earmark-excel"></i>
                        </h3>
                        <h6 className="ev-fs-12 mb-1">
                          Excel Documents
                        </h6>
                      </div>
                      {renderFilePicker('excelFiles', '.xls,.xlsx, .xlsm')}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="ev-card-3 border-2 p-2">
                    <div className="card-body p-1">
                      <div className ="text-success">
                        <h3 className="mb-2">
                        <i className="bi bi-filetype-csv"></i>
                        </h3>
                        <h6 className="ev-fs-12 mb-1">
                          CSV Documents
                        </h6>
                      </div>
                      {renderFilePicker('csvFiles', '.csv')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {editingThread ? (
              <div className="alert alert-info mt-0" >
                <i className="bi bi-info-circle me-1"></i>
                <b>Note:</b> Uploading new documents will add them to the existing Data Folder. 
              </div>
            ) : (
              <div className="alert alert-primary mt-0">
                <i className="bi bi-info-circle me-1"></i>
                <b>Note:</b> You can always upload new documents later by editing this Data folders.
              </div>
            )}
            <div className="mt-2 text-end" >
              <button type="submit" className="ev-btn-blue-fill-round ev-bold-reg" disabled={isExtracting}>
                <i className="bi bi-cursor-fill"></i> {editingThread ? 'Update Data Folder' : 'Create Data Folder'}
              </button>
              <button 
                type="button" 
                className="ev-btn-blue-outline-round ms-2" 
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
      <RefExtractionModal
        show={showRefModal}
        onClose={() => setShowRefModal(false)}
        files={files}
        refExtractFiles={refExtractFiles}
        setRefExtractFiles={setRefExtractFiles}
      />

      <LinkedProjectsModal
        show={showLinkedProjects}
        onClose={() => setShowLinkedProjects(false)}
        projects={linkedProjects}
        threadName={editingThread?.name}
      />
    </div>
  );
};

export default UnstructuredThreadForm;
